import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import DatasheetPreview from './DatasheetPreview';
import USDatasheetPreview from './USDatasheetPreview';
import { useRegion } from '../region';
import { fmtDateTime } from '../timefmt';

const STATUS_STYLE = {
  pending: { bg: '#fffbeb', bd: '#f59e0b', label: 'Pending review' },
  denied: { bg: '#fef2f2', bd: '#dc2626', label: 'Changes requested' },
  approved: { bg: '#f0fdf4', bd: '#16a34a', label: 'Approved' },
};

export default function ApprovalsView({ toast, canReview, openInEditor }) {
  const region = useRegion();
  const [items, setItems] = useState(null);
  const [mode, setMode] = useState(canReview ? 'queue' : 'mine');
  const [preview, setPreview] = useState(null);
  const [deny, setDeny] = useState(null); // product being denied
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setItems(null);
    const r = await api.listApprovals(mode === 'mine');
    setItems(r.items || []);
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  async function decide(p, approve) {
    setBusy(p.id);
    try {
      if (approve) { await api.approveDatasheet(p.id, ''); toast('"' + (p.name || 'Untitled') + '" approved & live'); }
      else { await api.denyDatasheet(p.id, notes); toast('Sent back to ' + (p.submitted_by_name || 'the submitter') + ' with your notes'); }
      setDeny(null); setNotes('');
      load();
    } catch (e) { toast(e?.response?.data?.detail || 'Action failed', true); }
    setBusy('');
  }

  async function openPreview(p) {
    try { setPreview(await api.getProduct(p.id)); }
    catch (e) { toast('Could not load preview', true); }
  }

  return (
    <div className="pc-view" data-testid="approvals-view">
      <div className="pc-head">
        <div>
          <h2>Datasheet Approvals</h2>
          <p className="pc-sub">{canReview
            ? 'Sales Support submissions to the shared libraries land here. Preview each one, then approve it live, send it back with notes, or edit it yourself and approve.'
            : 'Your Core Range / Customer datasheet submissions. Denied ones come back with reviewer notes — edit and resubmit.'}</p>
        </div>
        {canReview && (
          <div className="si-focus-tabs">
            <button className={'si-focus-tab' + (mode === 'queue' ? ' on' : '')} data-testid="approvals-tab-queue" onClick={() => setMode('queue')}>Review queue</button>
            <button className={'si-focus-tab' + (mode === 'mine' ? ' on' : '')} data-testid="approvals-tab-mine" onClick={() => setMode('mine')}>My submissions</button>
          </div>
        )}
      </div>

      {items === null && <div className="empty-note">Loading…</div>}
      {items && items.length === 0 && (
        <div className="empty-note" data-testid="approvals-empty">
          {mode === 'queue' ? 'Nothing waiting for review — all clear.' : 'No submissions yet — save a datasheet to Core Range or Customer Specific and it will appear here.'}
        </div>
      )}
      <div style={{ display: 'grid', gap: 12, maxWidth: 860 }}>
        {(items || []).map(p => {
          const st = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
          return (
            <div key={p.id} className="dash-card" data-testid={'approval-' + p.id}
              style={{ padding: 14, borderLeft: '4px solid ' + st.bd }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <strong>{p.name || '(Untitled)'}</strong>
                  <div className="ps-muted" style={{ fontSize: 12 }}>
                    {[p.code, p.scope === 'core' ? 'Core Range' : 'Customer Specific',
                      p.submitted_by_name ? 'by ' + p.submitted_by_name : '',
                      p.updated_at ? fmtDateTime(p.updated_at) : ''].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span className="pc-ds-pill" style={{ background: st.bg, borderColor: st.bd, color: '#333' }} data-testid={'approval-status-' + p.id}>{st.label}</span>
              </div>
              {p.review_notes && (
                <div data-testid={'approval-notes-' + p.id} style={{ margin: '8px 0', padding: '8px 10px', background: '#fef9c3', borderRadius: 6, fontSize: 12.5 }}>
                  <b>Reviewer notes{p.reviewed_by ? ' (' + p.reviewed_by + ')' : ''}:</b> {p.review_notes}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-sm" data-testid={'approval-preview-' + p.id} onClick={() => openPreview(p)}>👁 Preview</button>
                {mode === 'queue' ? (
                  <>
                    <button className="btn btn-sm" data-testid={'approval-edit-' + p.id} onClick={() => openInEditor(p.id)}>✎ Edit myself</button>
                    <button className="btn btn-primary btn-sm" data-testid={'approval-approve-' + p.id} disabled={busy === p.id} onClick={() => decide(p, true)}>✓ Approve & deploy</button>
                    <button className="btn btn-sm btn-danger" data-testid={'approval-deny-' + p.id} onClick={() => { setDeny(p); setNotes(''); }}>✕ Send back</button>
                  </>
                ) : (
                  p.status !== 'approved' && <button className="btn btn-primary btn-sm" data-testid={'approval-resubmit-' + p.id} onClick={() => openInEditor(p.id)}>✎ Edit & resubmit</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <div className="modal-overlay" data-testid="approval-preview-modal" onClick={() => setPreview(null)}>
          <div className="modal" style={{ maxWidth: 900, width: '92vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{preview.name} <span className="ps-muted" style={{ fontSize: 13 }}>{preview.code}</span></h3>
              <button className="modal-close" data-testid="approval-preview-close" onClick={() => setPreview(null)}>×</button>
            </div>
            <div style={{ transform: 'scale(0.85)', transformOrigin: 'top left' }}>
              {region === 'US' ? <USDatasheetPreview sheet={preview.data} pageId="apr-pv" /> : <DatasheetPreview sheet={preview.data} pageId="apr-pv" />}
            </div>
          </div>
        </div>
      )}

      {deny && (
        <div className="modal-overlay" data-testid="approval-deny-modal" onClick={() => setDeny(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Send "{deny.name || 'Untitled'}" back</h3>
            <p className="modal-hint">Your notes go to {deny.submitted_by_name || 'the submitter'} so they know what to change.</p>
            <textarea className="input" data-testid="approval-deny-notes" rows={4} style={{ resize: 'vertical' }}
              placeholder="e.g. Wattage table is missing the 240W variant, and the photo is low-res…"
              value={notes} onChange={e => setNotes(e.target.value)} />
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setDeny(null)}>Cancel</button>
              <button className="btn btn-danger-solid" data-testid="approval-deny-confirm" disabled={busy === deny.id} onClick={() => decide(deny, false)}>Send back with notes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
